/**
 * RevenueCat bridge (Capacitor).
 *
 * Encapsulates SDK init, user identification, offerings fetch, purchase,
 * restore, and backend sync. Gracefully degrades on web — every function
 * is a no-op (or throws a clean error) when Capacitor.isNativePlatform()
 * is false, so the rest of the codebase doesn't have to platform-check.
 *
 * SDK keys + Purchases.configure() live in services/revenuecat.js (shared
 * with the Car Owner `owner_premium` flow). Keys come from build-time env
 * vars REACT_APP_REVENUECAT_IOS_KEY / REACT_APP_REVENUECAT_ANDROID_KEY, or
 * the placeholder constants in that file.
 *
 * Entitlement names (must match the RevenueCat dashboard entitlements):
 *   `pro`     → 3-job tier ($59.99/mo)
 *   `premium` → unlimited tier ($79.99/mo)
 *
 * Package identifiers in the default offering:
 *   `pro_monthly`
 *   `premium_monthly`
 */
import { api } from "@/lib/api";
import {
  isRevenueCatConfigured,
  ensureRevenueCatConfigured,
  initRevenueCat,
  logOutRevenueCat,
} from "@/services/revenuecat";
import { shopPlanAccess, syncRefusedMessage } from "@/lib/shopPurchaseGate";

// SDK keys + configure() now live in services/revenuecat.js (single source of
// truth, shared with the Car Owner owner_premium flow).

// Tier ID → (entitlement name, package identifier on RC).
const TIER_CONFIG = {
  pro: { entitlement: "pro", packageId: "pro_monthly" },
  premium: { entitlement: "premium", packageId: "premium_monthly" },
};

let _initialized = false;

export function isIapAvailable() {
  return isRevenueCatConfigured();
}

/** Initialize the RevenueCat SDK. Idempotent. */
export async function initIap() {
  if (_initialized) return;
  _initialized = await ensureRevenueCatConfigured();
}

export async function identifyIap(userId) {
  if (!isIapAvailable() || !userId) return;
  await initRevenueCat(userId);
  _initialized = true;
}

export async function logOutIap() {
  await logOutRevenueCat();
}

/** Fetch the package for a specific tier ('pro' | 'premium'). */
export async function getTierPackage(tier) {
  if (!isIapAvailable()) return null;
  const cfg = TIER_CONFIG[tier];
  if (!cfg) return null;
  if (!_initialized) await initIap();
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const offerings = await Purchases.getOfferings();
    const current = offerings?.current;
    if (!current) return null;
    const pkgs = current.availablePackages || [];
    return (
      pkgs.find((p) => p.identifier === cfg.packageId)
      || pkgs.find((p) => p.identifier?.toLowerCase().includes(tier))
      || null
    );
  } catch (e) {
    console.error("RevenueCat getOfferings failed:", e);
    return null;
  }
}

/** Back-compat alias for the old single-tier helper. */
export const getPremiumPackage = () => getTierPackage("premium");

/** Execute a purchase for the specified tier.
 * shop-status-fixes-nov2 (H1): pass `{ user }` so a pending / rejected /
 * suspended shop never reaches the store sheet; a 403 from the backend sync
 * is returned as a visible failure (never "Welcome to Premium"). */
export async function purchaseTier(tier, opts = {}) {
  if (!isIapAvailable()) {
    return { ok: false, error: "IAP not available on this platform." };
  }
  if (opts.user) {
    const gate = shopPlanAccess(opts.user);
    if (!gate.allowed) return { ok: false, blocked: true, error: gate.message };
  }
  const cfg = TIER_CONFIG[tier];
  if (!cfg) return { ok: false, error: `Unknown tier: ${tier}` };
  const pkg = await getTierPackage(tier);
  if (!pkg) return { ok: false, error: `No package available for ${tier}.` };
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    const customerInfo = result?.customerInfo;
    const sync = await syncIapToBackend(customerInfo);
    if (sync.refused) {
      return { ok: false, syncRefused: true, error: syncRefusedMessage(sync.detail) };
    }
    return {
      ok: true,
      customerInfo,
      tier,
      premiumUntil:
        customerInfo?.entitlements?.active?.[cfg.entitlement]?.expirationDate || null,
    };
  } catch (e) {
    if (e?.userCancelled) return { ok: false, userCancelled: true };
    console.error("Purchase failed:", e);
    return { ok: false, error: e?.message || "Purchase failed." };
  }
}

/** Back-compat alias — purchases Premium tier specifically. */
export const purchasePremium = () => purchaseTier("premium");

/** Restore previous purchases (e.g., after reinstall). */
export async function restorePurchases(opts = {}) {
  if (!isIapAvailable()) {
    return { ok: false, error: "IAP not available on this platform." };
  }
  if (opts.user) {
    const gate = shopPlanAccess(opts.user);
    if (!gate.allowed) return { ok: false, blocked: true, error: gate.message };
  }
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const { customerInfo } = await Purchases.restorePurchases();
    const active = !!(
      customerInfo?.entitlements?.active?.premium
      || customerInfo?.entitlements?.active?.pro
    );
    if (active) {
      const sync = await syncIapToBackend(customerInfo);
      if (sync.refused) {
        return { ok: false, syncRefused: true, error: syncRefusedMessage(sync.detail) };
      }
    }
    return { ok: true, restored: active, customerInfo };
  } catch (e) {
    console.error("Restore failed:", e);
    return { ok: false, error: e?.message || "Restore failed." };
  }
}

/** Returns { ok } on success, { ok: false, refused: true, detail } when the
 * backend REFUSED the sync (403: shop not approved), or { ok: false } for a
 * transient failure (network / 5xx: the RevenueCat webhook will fix it). */
export async function syncIapToBackend(customerInfo) {
  if (!customerInfo) return { ok: true };
  try {
    await api.post("/billing/iap/sync", { customer_info: customerInfo });
    return { ok: true };
  } catch (e) {
    const status = e?.response?.status;
    if (status === 403) {
      return { ok: false, refused: true, status, detail: e?.response?.data?.detail };
    }
    console.warn("Backend IAP sync failed (webhook will fix it):", e?.message || e);
    return { ok: false, status };
  }
}
